// Route data prefetch. The shop and product pages are lazy chunks, so their
// first GET used to start only after the chunk had downloaded and rendered.
// Here the same GET (same URL + auth, see utils/api primeGetJson) starts as
// soon as main.js runs, in parallel with the chunk, and the page's own call
// picks up the answer. Links do the same on hover (mouse) or touch.

import { prefetchProduct, prefetchProducts } from './api';
import { firstPageRequest } from './shopQuery';
import { DEFAULT_VAT_RATE } from './vat';

const PRODUCT_PATH = /^\/products\/([A-Za-z0-9_-]{1,64})\/?$/;
const SHOP_PATH = /^\/products\/?$/;

const loadShopChunk = () => import('../pages/LuxuryProducts');
const loadProductChunk = () => import('../pages/ProductDetail');

/**
 * Start the first data request for `location` ({pathname, search}).
 * Returns what was started: 'products' | 'product' | null.
 */
export const prefetchForLocation = ({ pathname = '', search = '' } = {}) => {
  if (SHOP_PATH.test(pathname)) {
    const params = new URLSearchParams(search);
    // A price filter is converted with the server's VAT rate, which is not
    // known yet: the page asks itself rather than risk a wasted request.
    if (params.has('min_price') || params.has('max_price')) return null;
    prefetchProducts(firstPageRequest(params, DEFAULT_VAT_RATE));
    return 'products';
  }
  const match = PRODUCT_PATH.exec(pathname);
  if (match) {
    prefetchProduct(match[1]);
    return 'product';
  }
  return null;
};

const saveData = () => {
  try {
    return Boolean(navigator.connection && navigator.connection.saveData);
  } catch {
    return false;
  }
};

/** Warm the chunk + data of a same-origin shop or product link. */
export const prefetchLink = (anchor) => {
  if (!anchor || !anchor.href || anchor.target === '_blank') return null;
  let url;
  try {
    url = new URL(anchor.href, window.location.href);
  } catch {
    return null;
  }
  if (url.origin !== window.location.origin) return null;
  if (url.pathname === window.location.pathname && url.search === window.location.search) return null;
  const started = prefetchForLocation(url);
  if (started === 'product') loadProductChunk().catch(() => {});
  if (started === 'products' || SHOP_PATH.test(url.pathname)) loadShopChunk().catch(() => {});
  return started;
};

let installed = false;

/**
 * One delegated listener set for the whole document: a mouse hovering a link
 * or a finger touching it. Nothing on Save-Data. Returns an uninstall function.
 */
export const installIntentPrefetch = () => {
  if (installed || typeof document === 'undefined' || saveData()) return () => {};
  installed = true;
  const warmed = new Set();
  const onIntent = (event) => {
    if (event.type === 'pointerover' && event.pointerType !== 'mouse') return;
    const anchor = event.target && event.target.closest ? event.target.closest('a[href]') : null;
    if (!anchor || warmed.has(anchor.href)) return;
    warmed.add(anchor.href);
    prefetchLink(anchor);
  };
  document.addEventListener('pointerover', onIntent, { passive: true });
  document.addEventListener('touchstart', onIntent, { passive: true });
  return () => {
    installed = false;
    document.removeEventListener('pointerover', onIntent);
    document.removeEventListener('touchstart', onIntent);
  };
};
